import React, { useState, useEffect, useCallback } from "react";
import CopyToClipboardWrapper from "../../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import styled from "styled-components/macro";
import {
  Typography as MuiTypography,
  Button as MuiButton,
  Box as MuiBox
} from "@mui/material";
import { spacing, SpacingProps } from "@mui/system";
import "react-dragula/dist/dragula.css";
import CommonPageLayout from "../../../components/commonPageLayout/commonPageLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFormattedDate,
  getLastDigitFromUserId
} from "../../../utils/commonFunctions";
import Dropdown from "../../../components/dropdown/Dropdown";
import { UserLocations } from "../../../utils/constants";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";

const Box = styled(MuiBox)``;
interface ButtonProps extends SpacingProps {
  component?: React.ElementType;
  to?: string;
  target?: string;
}
const Button = styled(MuiButton)<ButtonProps>(spacing);

const CustomButton: any = styled(MuiButton)`
  color: #223354;
  &.blue_button {
    background-color: blue;
  }

  &.custom_disabled {
    background-color: grey !important;
  }
`;
interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

const TypographyTitle = styled(MuiTypography)`
  color: var(--light-text-dark, rgba(0,0,0,0.88)));
  font-family: 'Montserrat',sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

function UserCardPanels() {
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const accessToken: any = localStorage?.getItem("accessToken");

  // Use userId if available (from UserDetailsPage), otherwise use id
  const userIdentifier = userId || id;

  // Local state management
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<any>("prepaid");

  const fetchData = useCallback(
    async (accessToken: string, searchTerm: string) => {
      if (!userIdentifier) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${apiEndpoint}/cardpanels/cardpanels/user/${userIdentifier}/${searchTerm}`,
          {
            headers: {
              Authorization: `bearer ${accessToken}`,
              "Content-Type": "application/json"
            }
          }
        );

        // Set data directly from response
        setData(response.data || []);
      } catch (error) {
        console.error("Failed to fetch card panels:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [userIdentifier]
  );

  useEffect(() => {
    fetchData(accessToken, searchTerm);
  }, [searchTerm, accessToken, fetchData]);

  // Define columns for the table
  const columns = [
    {
      id: "CardPanelId",
      label: "CardPanelId",
      minWidth: 200,
      align: "left" as const,
      format: (value: any, row: any) => (
        <div style={{ display: "flex", alignItems: "left" }}>
          <div
            style={{
              verticalAlign: "middle",
              display: "flex",
              alignItems: "center"
            }}
          >
            {value ? getLastDigitFromUserId(value) : "-"}
          </div>
          {value && (
            <div onClick={(e) => e?.stopPropagation()}>
              <CopyToClipboardWrapper text={value} cell="CardPanelId" />
            </div>
          )}
        </div>
      )
    },
    {
      id: "CardPanelScheduleId",
      label: "CardPanelScheduleId",
      minWidth: 200,
      align: "left" as const,
      format: (value: any, row: any) => (
        <div style={{ display: "flex", alignItems: "left" }}>
          <div
            style={{
              verticalAlign: "middle",
              display: "flex",
              alignItems: "center"
            }}
          >
            {value ? getLastDigitFromUserId(value) : "-"}
          </div>
          {value && (
            <div onClick={(e) => e?.stopPropagation()}>
              <CopyToClipboardWrapper text={value} cell="CardPanelScheduleId" />
            </div>
          )}
        </div>
      )
    },
    {
      id: "Descriptor",
      label: "Descriptor",
      minWidth: 200,
      align: "left" as const,
      format: (value: any) => value || "-"
    },
    {
      id: "Location",
      label: "Location",
      minWidth: 200,
      align: "left" as const,
      format: (value: any) => value || "-"
    },
    {
      id: "StartDate",
      label: "StartDate",
      minWidth: 200,
      align: "left" as const,
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "EndDate",
      label: "EndDate",
      minWidth: 200,
      align: "left" as const,
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    }
  ];

  return (
    <>
      <TypographyTitle>{"Filter user card panels by location"}</TypographyTitle>

      <Box>
        <Dropdown
          title={"Location"}
          name={"Location"}
          options={UserLocations}
          value={searchTerm}
          label={"Location"}
          callback={(e: any) => setSearchTerm(e?.target.value)}
        />
      </Box>

      <Box mt={2}>
        <CustomTableWithPagination
          columns={columns}
          data={data}
          apiStatus={loading ? "fetching" : "idle"}
          isShowPagination={false}
          noDataFoundMessage="No Data Available"
          handleRowClick={() => {}}
          sx={{ cursor: "default" }}
        />
      </Box>
    </>
  );
}

export default UserCardPanels;
